import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatError, money, num } from "@/lib/api";
import { StatusPill } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Kpi, Panel, NavyBtn } from "@/pages/portals/ui";

function Assigned({ p, refresh }) {
  const [f, setF] = useState({ construction_status: p.construction_status || "Pending", fence_ft_installed: p.fence_ft_installed || 0, note: "" });
  const save = async () => {
    try { await api.patch(`/portal/fence/projects/${p.id}`, { ...f, fence_ft_installed: Number(f.fence_ft_installed) }); toast.success("Project updated"); setF({ ...f, note: "" }); refresh(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="rounded-2xl border border-sky-100 p-4" data-testid={`fence-assigned-${p.code}`}>
      <div className="flex justify-between items-center"><p className="font-bold text-[#03045E]">{p.code} — {p.name}</p><StatusPill status={p.status} /></div>
      <p className="text-xs text-slate-500">Perimeter ~{num(p.perimeter_ft)} ft • Materials budget {money(p.budget?.find((b) => b.key === "materials")?.amount)}</p>
      <div className="grid sm:grid-cols-3 gap-2 mt-3">
        <Input value={f.construction_status} onChange={(e) => setF({ ...f, construction_status: e.target.value })} placeholder="Construction status" data-testid={`fence-status-${p.code}`} />
        <Input type="number" value={f.fence_ft_installed} onChange={(e) => setF({ ...f, fence_ft_installed: e.target.value })} placeholder="Feet installed" data-testid={`fence-feet-${p.code}`} />
        <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Progress note (public)" data-testid={`fence-note-${p.code}`} />
      </div>
      <NavyBtn onClick={save} className="mt-3" data-testid={`fence-save-${p.code}`}>Save update</NavyBtn>
    </div>
  );
}

function Available({ p, refresh, bid }) {
  const [est, setEst] = useState("");
  const [notes, setNotes] = useState("");
  const submit = async () => {
    try { await api.post("/portal/fence/bids", { project_code: p.code, estimate: Number(est), notes }); toast.success("Estimate submitted"); refresh(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="rounded-2xl border border-sky-100 p-4" data-testid={`fence-available-${p.code}`}>
      <div className="flex justify-between items-center"><p className="font-bold text-[#03045E]">{p.code} — {p.name}</p><StatusPill status={p.status} /></div>
      <p className="text-xs text-slate-500">{[p.neighborhood, p.zip].filter(Boolean).join(", ")} • Perimeter ~{num(p.perimeter_ft)} ft</p>
      {bid ? <p className="text-sm font-bold text-green-600 mt-3">Estimate submitted: {money(bid.estimate)} ({bid.status})</p> : (
        <div className="flex flex-wrap gap-2 mt-3">
          <Input className="w-36" type="number" placeholder="Estimate $" value={est} onChange={(e) => setEst(e.target.value)} data-testid={`fence-bid-amount-${p.code}`} />
          <Input className="flex-1 min-w-[160px]" placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`fence-bid-notes-${p.code}`} />
          <NavyBtn disabled={!est} onClick={submit} data-testid={`fence-bid-submit-${p.code}`}>Submit estimate</NavyBtn>
        </div>
      )}
    </div>
  );
}

export default function FencePortal({ user }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["portal", "fence"], queryFn: () => api.get("/portal/fence").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["portal", "fence"] });
  if (!data) return <p>Loading…</p>;
  return (
    <div className="space-y-6" data-testid="fence-portal">
      <div className="rounded-3xl sky-bg border border-sky-100 p-6"><p className="font-display uppercase text-2xl text-[#03045E]">{user.org_name || user.name}</p><p className="text-sm font-bold text-[#0096C7] uppercase tracking-wider">Swim for Rakim Community Fence Partner™</p></div>
      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Projects completed" value={data.stats.completed} testid="fence-kpi-completed" />
        <Kpi label="Feet installed" value={num(data.stats.feet)} testid="fence-kpi-feet" />
        <Kpi label="Communities served" value={data.stats.communities} testid="fence-kpi-communities" />
      </div>
      <Panel title="Assigned projects" testid="fence-assigned">
        <div className="space-y-3">{data.assigned.length ? data.assigned.map((p) => <Assigned key={p.id} p={p} refresh={refresh} />) : <p className="text-sm text-slate-500">No projects assigned yet. Submit estimates below.</p>}</div>
      </Panel>
      <Panel title="Available projects — request to estimate" testid="fence-available">
        <div className="space-y-3">{data.available.length ? data.available.map((p) => <Available key={p.code} p={p} refresh={refresh} bid={data.bids.find((b) => b.project_code === p.code)} />) : <p className="text-sm text-slate-500">No projects are open for estimates right now.</p>}</div>
      </Panel>
    </div>
  );
}
